import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { collection, addDoc } from 'firebase/firestore';
import { db, OperationType, handleFirestoreError } from '../lib/firebase';
import { MessageSquare, X, Send, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function WhatsAppFAB() {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  
  // Form State
  const [nombre, setNombre] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [correo, setCorreo] = useState('');
  const [mensaje, setMensaje] = useState('');
  
  // Validation / Error States
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!nombre.trim()) {
      newErrors.nombre = language === 'es' ? 'El nombre es obligatorio' : 'Name is required';
    }

    const cleanedWhatsapp = whatsapp.replace(/\D/g, '');
    if (!cleanedWhatsapp) {
      newErrors.whatsapp = language === 'es' ? 'El número es obligatorio' : 'Phone is required';
    } else if (cleanedWhatsapp.length !== 10) {
      newErrors.whatsapp = language === 'es' ? 'Debe tener exactamente 10 dígitos' : 'Must be exactly 10 digits';
    }

    if (!correo.trim()) {
      newErrors.correo = language === 'es' ? 'El correo es obligatorio' : 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      newErrors.correo = language === 'es' ? 'Formato de correo no válido' : 'Invalid email format';
    }

    if (!mensaje.trim()) {
      newErrors.mensaje = language === 'es' ? 'El mensaje es obligatorio' : 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // Save info in Firestore collection 'whatsapp-leads'
      await addDoc(collection(db, 'whatsapp-leads'), {
        nombre: nombre.trim(),
        whatsapp: whatsapp.replace(/\D/g, ''),
        correo: correo.trim().toLowerCase(),
        mensaje: mensaje.trim(),
        createdAt: new Date().toISOString()
      });

      // Dispatch email notification to the Treid team
      try {
        fetch('/api/notify-whatsapp-lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nombre: nombre.trim(),
            whatsapp: whatsapp.trim(),
            correo: correo.trim().toLowerCase(),
            mensaje: mensaje.trim()
          })
        }).catch(err => console.warn("WhatsApp notification error:", err));
      } catch (notifyErr) {
        console.warn("Could not dispatch WhatsApp notification:", notifyErr);
      }

      // Clear form & close modal
      setNombre('');
      setWhatsapp('');
      setCorreo('');
      setMensaje('');
      setIsOpen(false);

      // WhatsApp link preparation
      // Treid target sales phone number. You can update this phone number as needed.
      const targetPhone = '5214444437625'; 
      const customMessage = language === 'es' 
        ? `Hola Treid, estoy interesado en sus servicios. Mi nombre es: ${nombre}. Mi de teléfono/WhatsApp es: ${whatsapp}. Correo: ${correo}. Mensaje: ${mensaje}`
        : `Hello Treid, I am interested in your services. My name is: ${nombre}. My phone/WhatsApp is: ${whatsapp}. Email: ${correo}. Message: ${mensaje}`;
      
      const whatsappURL = `https://wa.me/${targetPhone}?text=${encodeURIComponent(customMessage)}`;
      
      // Redirect to WhatsApp
      window.open(whatsappURL, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error("Error saving lead to Firestore:", err);
      try {
        handleFirestoreError(err, OperationType.CREATE, 'whatsapp-leads');
      } catch (e) {
        // Fallback to presenting the alert so the user interface remains interactive
        alert(language === 'es' ? "Error al registrar tus datos. Por favor, inténtalo de nuevo." : "Error saving registration. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits up to 10 characters
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setWhatsapp(val);
  };

  return (
    <>
      {/* Floating Action Button - ONLY visible on Mobile / Small screens (< md) */}
      <div className="fixed bottom-6 right-6 z-40 block md:hidden">
        <motion.button
          id="whatsapp-mobile-fab"
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl cursor-pointer focus:outline-none relative"
          aria-label="Contactar por WhatsApp"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <MessageSquare size={26} className="fill-white" />
        </motion.button>
      </div>

      {/* Mini Form Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            {/* Backdrop */}
            <motion.div
              id="whatsapp-modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Body */}
            <motion.div
              id="whatsapp-modal-container"
              initial={{ y: "100%", opacity: 0.8 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0.8 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              className="relative w-full max-w-lg bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 sm:border sm:rounded-2xl shadow-2xl p-6 overflow-hidden max-h-[85vh] overflow-y-auto z-10"
            >
              <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 pb-4 mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-[#25D366]/10 dark:bg-[#25D366]/20 flex items-center justify-center text-[#25D366]">
                    <Phone size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-zinc-900 dark:text-white">
                      {language === 'es' ? 'Contactar por WhatsApp' : 'Contact via WhatsApp'}
                    </h3>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {language === 'es' ? 'Completa los datos para iniciar el chat' : 'Fill in the form to start the direct chat'}
                    </p>
                  </div>
                </div>
                <button
                  id="close-whatsapp-modal"
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800/60 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 dark:hover:text-white transition-colors focus:outline-none"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full Name */}
                <div>
                  <label htmlFor="wa-nombre" className="block text-xs font-semibold text-zinc-650 dark:text-zinc-300 mb-1.5 uppercase tracking-wider">
                    {language === 'es' ? 'Nombre Completo' : 'Full Name'}
                  </label>
                  <input
                    type="text"
                    id="wa-nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder={language === 'es' ? 'Ingresa tu nombre' : 'Enter your name'}
                    className={`w-full bg-zinc-50 dark:bg-zinc-950 border ${errors.nombre ? 'border-rose-500' : 'border-zinc-200 dark:border-zinc-800 focus:border-primary'} rounded-xl p-3 text-sm text-zinc-900 dark:text-white focus:outline-none transition-colors`}
                  />
                  {errors.nombre && (
                    <p className="text-xs text-rose-500 mt-1 font-semibold">{errors.nombre}</p>
                  )}
                </div>

                {/* WhatsApp Phone */}
                <div>
                  <label htmlFor="wa-phone" className="block text-xs font-semibold text-zinc-650 dark:text-zinc-300 mb-1.5 uppercase tracking-wider">
                    {language === 'es' ? 'Tu WhatsApp (10 dígitos)' : 'Your WhatsApp (10 digits)'}
                  </label>
                  <input
                    type="tel"
                    id="wa-phone"
                    value={whatsapp}
                    onChange={handlePhoneChange}
                    placeholder="e.g. 4441234567"
                    className={`w-full bg-zinc-50 dark:bg-zinc-950 border ${errors.whatsapp ? 'border-rose-500' : 'border-zinc-200 dark:border-zinc-800 focus:border-primary'} rounded-xl p-3 text-sm text-zinc-900 dark:text-white font-mono focus:outline-none transition-colors`}
                  />
                  {errors.whatsapp && (
                    <p className="text-xs text-rose-500 mt-1 font-semibold">{errors.whatsapp}</p>
                  )}
                </div>

                {/* Email Address */}
                <div>
                  <label htmlFor="wa-email" className="block text-xs font-semibold text-zinc-650 dark:text-zinc-300 mb-1.5 uppercase tracking-wider">
                    {language === 'es' ? 'Correo Electrónico' : 'Email Address'}
                  </label>
                  <input
                    type="email"
                    id="wa-email"
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className={`w-full bg-zinc-50 dark:bg-zinc-950 border ${errors.correo ? 'border-rose-500' : 'border-zinc-200 dark:border-zinc-800 focus:border-primary'} rounded-xl p-3 text-sm text-zinc-900 dark:text-white focus:outline-none transition-colors`}
                  />
                  {errors.correo && (
                    <p className="text-xs text-rose-500 mt-1 font-semibold">{errors.correo}</p>
                  )}
                </div>

                {/* Message Header */}
                <div>
                  <label htmlFor="wa-mensaje" className="block text-xs font-semibold text-zinc-650 dark:text-zinc-300 mb-1.5 uppercase tracking-wider">
                    {language === 'es' ? 'Mensaje Inicial' : 'Initial Message'}
                  </label>
                  <textarea
                    id="wa-mensaje"
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    placeholder={language === 'es' ? '¿En qué podemos ayudarte?' : 'How can we help you?'}
                    rows={3}
                    className={`w-full bg-zinc-50 dark:bg-zinc-950 border ${errors.mensaje ? 'border-rose-500' : 'border-zinc-200 dark:border-zinc-800 focus:border-primary'} rounded-xl p-3 text-sm text-zinc-900 dark:text-white focus:outline-none transition-colors resize-none`}
                  />
                  {errors.mensaje && (
                    <p className="text-xs text-rose-500 mt-1 font-semibold">{errors.mensaje}</p>
                  )}
                </div>

                {/* Call to action button */}
                <button
                  id="wa-submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-2 bg-[#25D366] hover:bg-[#20ba5a] text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg active:scale-98 disabled:opacity-50 disabled:scale-100 cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin"></span>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>{language === 'es' ? 'Enviar y Contactar' : 'Send & Contact'}</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
